import {
  Button,
  DialogContent,
  DialogTitle,
  Modal,
  Input,
  Flex,
  Text,
  Icon
} from "@theorgchartdev/storybook";
import CentralizedModalContainer from "../../../../Utils/CentralizedModalContainer/CentralizedModalContainer";
import React, { useState, useEffect } from "react";
import { GetWSMgr } from "@/Utils/StateLib";
import { useTopic } from "@/hooks/useTopic";
import { appstr } from "@coreapp/utils/appstr";
import { QAlert } from "@coreapp/CoreApp/QAlert";
import { QMFARFC } from "@coreapp/CoreApp/QMFARFC";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumMFAState } from "@coreapp/constants";
import { ValidateConfirmCode } from "@coreapp/utils/ValidateConfirmCode";

export const MfaModal = ({ show, onClose }) => {
  const wsMgr = GetWSMgr();
  const userInfo = wsMgr?.GetUserInfo();
  const accountName = wsMgr?.GetAccountName();
  const userName = wsMgr?.GetUserName();

  const [authCode, setAuthCode] = useState("");
  const [qrCode, setQRCode] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showMFAModal, setShowMFAModal] = useState(true);

  useEffect(() => {
    GetQRCode("qrCode");
  }, []);

  if (!userInfo) return;

  const DoMfaRFC = (mode, reset = false, confirmCode = null) => {
    const callbackObj = {
      DisplayQRCode: result => {
        const qrCodeWrapper = result != "MFA Disabled" ? result : null;
        HandleQRCodeResponse(qrCodeWrapper);
      }
    };
    const mfaRFC = new QMFARFC();
    mfaRFC.InitMFARFC(callbackObj);
    mfaRFC.SetParams(accountName, userName, mode, reset, confirmCode);
    Globals._rfcMgr.QueueRFC(mfaRFC);
  };

  const HandleQRCodeResponse = response => {
    const handled = StoreQRCode(response);
    HandleVerification(handled, response);
  };

  const StoreQRCode = newCode => {
    if (newCode == null || !["True", "False"].includes(newCode)) {
      setQRCode(newCode ? newCode : qrCode);
      return true;
    }
    return false;
  };

  const GetQRCode = () => {
    DoMfaRFC("getAuthenticator", true);
  };

  const VerifyMFA = () => {
    if (!ValidateConfirmCode(authCode)) {
      HandleResponseAlert(false);
      return;
    }
    DoMfaRFC("confirmCode", false, authCode);
  };

  const HandleVerification = (shouldExit, confirmation) => {
    if (shouldExit)
      // Should Exit indicates whether a QR Code was generated. Verification and QR Code generation are independent.
      return;
    const isValid = confirmation == "True";
    if (isValid) UpdateUserEnabled();
    HandleResponseAlert(isValid);
  };

  const HandleResponseAlert = isValid => {
    const msgStr = isValid ? appstr("validCode") : appstr("invalidCode");
    const resultAlert = new QAlert();
    const header = isValid ? appstr("Enabled") : appstr("Not Configured");
    resultAlert.InitAlert(header, msgStr, this, wsMgr);
    if (isValid) {
      setShowConfirmationModal(true);
      setShowMFAModal(false);
    } else {
      setShowConfirmationModal(false);
      setShowMFAModal(true);
    }
  };

  const UpdateUserEnabled = () => {
    wsMgr.fUserInfo.SetMFA(wsMgr, { backendMFAState: enumMFAState.enabledAuth });
  };

  const getQRCodeImgPath = () => {
    return "data:image/png;base64," + qrCode;
  };

  var apps = ["Authy", "Duo Mobile", "Google Authenticator", "Microsoft Authenticator"];
  var appStr = "";
  for (var app of apps.slice(0, apps.length - 1)) appStr += app + ", ";
  let step1Text = appstr("mfaStep1LongPre") + ": " + appStr + "or " + apps.slice(-1) + ".";

  const flexClass = "oc-settings-section-field";
  const contentClass = "oc-modal-content";
  const upperClass = "oc-record-display-label";

  const getConfirmationModal = () => {
    return (
      <Flex direction={"column"} gap={"12px"} className={contentClass}>
        <Text modifier={"bold"} size={"medium"}>
          {appstr("mfaActive")}
        </Text>
        <Text>{appstr("MFA Enabled")}</Text>
        <Button
          modifier={"primary"}
          className={"oc-special-secondary"}
          size={"medium"}
          onClick={() => ResetMFA()}
          dataTestId="account-settings-authorization-mfa-setup-reset-btn"
        >
          {appstr("resetAuth")}
        </Button>
      </Flex>
    );
  };
  const ResetMFA = () => {
    setShowMFAModal(true);
    setShowConfirmationModal(false);
  };

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="example-modal"
        show={show}
        minWidth={"medium"}
        maxWidth={"medium"}
        fillWidth={true}
        centerScreen={true}
      >
        <DialogTitle
          divider={true}
          onClose={() => onClose()}
          data-testid="account-settings-authorization-mfa-setup-close-btn"
        >
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"lock"} />
            {appstr("MFA Setup")}
          </span>
        </DialogTitle>
        <DialogContent>
          {showMFAModal && (
            <div>
              <Flex direction={"column"} gap={"12px"} className={contentClass}>
                <Text modifier={"bold"} size={"medium"}>
                  {appstr("step1") + "  - " + appstr("mfaStep1Short")}
                </Text>
                <Text>{step1Text}</Text>
                <Text modifier={"bold"} size={"medium"}>
                  {appstr("step2") + "  - " + appstr("mfaStep2Short")}
                </Text>
                <Flex direction={"row"}>
                  <Flex className={"oc-left-justify"}>
                    <Text>{appstr("mfaStep2Long")}</Text>
                  </Flex>
                  <Flex
                    className={"oc-right-justify"}
                    flex={"0 0 auto"}
                    width="100px"
                    height="100px"
                  >
                    {qrCode && <img src={getQRCodeImgPath()} alt="fireSpot" />}
                  </Flex>
                </Flex>
                <Text modifier={"bold"} size={"medium"}>
                  {appstr("step3") + "  - " + appstr("mfaConfirmCode")}
                </Text>
                <Text>{appstr("mfaStep3Long")}</Text>
              </Flex>
              <Flex direction={"row"} padding={"10px"}>
                <Input
                  inputOnly
                  label=""
                  placeholder=""
                  size="medium"
                  type="number"
                  onChange={e => setAuthCode(e.target.value)}
                  dataTestId="account-settings-authorization-mfa-setup-code-input"
                />
                <Flex className={"oc-right-justify"}>
                  <Button
                    modifier={"primary"}
                    className={"oc-special-secondary"}
                    size={"medium"}
                    onClick={() => VerifyMFA(authCode)}
                    dataTestId="account-settings-authorization-mfa-setup-verify-btn"
                  >
                    {appstr("mfaVerify")}
                  </Button>
                </Flex>
              </Flex>
            </div>
          )}
          {showConfirmationModal && (
            <Flex direction={"column"} gap={"12px"} className={contentClass}>
              <Flex direction={"row"} gap={"12px"}>
                <span className={"ocn-explorer-icon-label"}>
                  <Icon size={"medium"} name={"check"} modifier={"success"} />
                  <Text modifier={"bold"} size={"medium"}>
                    {appstr("mfaActive")}
                  </Text>
                </span>
              </Flex>
              <Text>{appstr("MFA Enabled")}</Text>
              <Button
                modifier={"primary"}
                className={"oc-special-secondary"}
                size={"medium"}
                onClick={() => ResetMFA()}
                dataTestId="account-settings-authorization-mfa-setup-reset-btn"
              >
                {appstr("resetAuth")}
              </Button>
            </Flex>
          )}
        </DialogContent>
      </Modal>
    </CentralizedModalContainer>
  );
};

export const MfaModalWrapper = () => {
  const [showMFAModal, setShowMFAModal] = useState(false);
  useTopic("MFAModal", setShowMFAModal);
  if (!showMFAModal) return;
  return <MfaModal show={showMFAModal} onClose={() => setShowMFAModal(false)} />;
};
